import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../../assets/Images/momentum-amp-logo.svg";
import { login, homeRouteFor } from "../../services/authApi";

const features = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="13 2 13 9 20 9" />
        <path d="M20 14.5V7l-7-5H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8" />
        <circle cx="18" cy="18" r="4" />
        <polyline points="16 18 17.5 19.5 20 17" />
      </svg>
    ),
    label: "Instant commercial auto submissions",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
    label: "AI-powered risk analysis & scoring",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="15" y2="17" />
      </svg>
    ),
    label: "Carrier and agency workspaces in one portal",
  },
];

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim() || !password) {
      setError("Enter your email and password to continue.");
      return;
    }

    setIsLoading(true);
    try {
      const user = await login(username.trim(), password);
      navigate(homeRouteFor(user), { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;

      // A missing env var is a setup problem, not a connectivity one — say so.
      if (!err?.response && /not configured/i.test(err?.message || "")) {
        setError(err.message);
        setIsLoading(false);
        return;
      }

      const unreachable = err?.code === "ERR_NETWORK" || !status;

      if (status === 401 || status === 400) {
        setError("Incorrect email or password.");
      } else if (status === 403) {
        setError("This account is deactivated. Contact your administrator.");
      } else if (unreachable) {
        setError("Can't reach the login service. Check that the API is running.");
      } else {
        setError(err?.response?.data?.message || "Something went wrong. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const handleNowCertsLogin = () => {
    const url = process.env.REACT_APP_Nowcerts_Url;
    if (!url) {
      setError("NowCerts sign-in isn't configured. Set REACT_APP_Nowcerts_Url in your .env.");
      return;
    }
    setIsRedirecting(true);
    window.open(url, "_self");
  };

  return (
    <div className="lp-root">
      {/* ── Left brand panel ── */}
      <div className="lp-brand">
        <div className="lp-orb lp-orb--a" />
        <div className="lp-orb lp-orb--b" />
        <div className="lp-orb lp-orb--c" />

        <div className="lp-brand-inner">
          <div className="lp-brand-eyebrow">Trucking Underwriting Portal</div>

          <h2 className="lp-brand-headline">
            Underwriting tools
            <br />
            built for the
            <br />
            road ahead.
          </h2>

          <p className="lp-brand-desc">
            Streamline your commercial trucking underwriting workflow — from submissions to bound
            policies — in one unified platform.
          </p>

          <ul className="lp-features">
            {features.map((f, i) => (
              <motion.li
                key={i}
                className="lp-feature-item"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.45, ease: "easeOut" }}
              >
                <span className="lp-feature-icon">{f.icon}</span>
                <span>{f.label}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right login panel ── */}
      <div className="lp-right">
        <div className="lp-grid-bg" />

        <motion.div
          className="lp-card"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="lp-logo-wrap">
            <img src={logo} alt="Momentum AMP" className="lp-logo" />
          </div>

          <div className="lp-card-header">
            <span className="lp-secure-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure Sign-In
            </span>
            <h1 className="lp-title">Welcome Back</h1>
            <p className="lp-subtitle">Sign in to your carrier or agency workspace</p>
          </div>

          {/* ── Credentials form ── */}
          <form className="lp-form" onSubmit={handleSubmit} noValidate>
            <div className="lp-field">
              <label className="lp-label" htmlFor="lp-username">
                Email
              </label>
              <div className="lp-input-wrap">
                <span className="lp-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 6L2 7" />
                  </svg>
                </span>
                <input
                  id="lp-username"
                  type="email"
                  className="lp-input"
                  placeholder="you@company.com"
                  autoComplete="username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="lp-field">
              <div className="lp-label-row">
                <label className="lp-label" htmlFor="lp-password">
                  Password
                </label>
                <button type="button" className="lp-link-btn" tabIndex={-1}>
                  Forgot password?
                </button>
              </div>
              <div className="lp-input-wrap">
                <span className="lp-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="lp-password"
                  type={showPassword ? "text" : "password"}
                  className="lp-input"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="lp-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <path d="M1 1l22 22M6.61 6.61A18.15 18.15 0 0 0 1 12s4 8 11 8a9.12 9.12 0 0 0 5.39-1.61" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <motion.div
                className="lp-error"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                role="alert"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="lp-submit"
              disabled={isLoading}
              whileHover={{ scale: isLoading ? 1 : 1.012, y: isLoading ? 0 : -2 }}
              whileTap={{ scale: isLoading ? 1 : 0.988 }}
              transition={{ type: "spring", stiffness: 420, damping: 26 }}
            >
              {isLoading ? (
                <>
                  <span className="lp-spinner" />
                  <span>Signing in…</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </motion.button>
          </form>

          <div className="lp-divider">
            <span>or</span>
          </div>

          {/* ── NowCerts SSO ── */}
          <motion.button
            type="button"
            className="lp-btn lp-btn--outline"
            onClick={handleNowCertsLogin}
            disabled={isRedirecting || isLoading}
            whileHover={{ scale: 1.012, y: -2 }}
            whileTap={{ scale: 0.988 }}
            transition={{ type: "spring", stiffness: 420, damping: 26 }}
          >
            {isRedirecting ? (
              <span className="lp-spinner lp-spinner--dark" />
            ) : (
              <>
                <span className="lp-btn-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L3 7v5c0 5.25 3.9 10.15 9 11.35C17.1 22.15 21 17.25 21 12V7L12 2z" fill="currentColor" fillOpacity="0.85" />
                    <polyline points="9 12 11 14 15 10" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="lp-btn-text">
                  <span className="lp-btn-label">Continue with</span>
                  <span className="lp-btn-brand">NowCerts</span>
                </span>
                <svg className="lp-btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
          </motion.button>
        </motion.div>

        <p className="lp-copyright">
          &copy; {new Date().getFullYear()} Trucking Underwriting Portal &nbsp;&middot;&nbsp; All rights reserved
        </p>
      </div>
    </div>
  );
};

export default Login;
